import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Paper } from '@mui/material';
import { fireSessionConfetti } from '@/utils/confetti';

interface VocabularySessionCompleteProps {
  totalReviewed: number;
  easyCount: number;
  needsWorkCount: number;
  onDone: () => void;
}

export function VocabularySessionComplete({
  totalReviewed,
  easyCount,
  needsWorkCount,
  onDone,
}: VocabularySessionCompleteProps) {
  const { t } = useTranslation('vocabulary');

  useEffect(() => {
    const cleanup = fireSessionConfetti();
    return cleanup;
  }, []);

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 4,
          textAlign: 'center',
          maxWidth: 420,
          width: '100%',
          background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)',
          border: '1px solid rgba(108,99,255,0.3)',
        }}
      >
        <Typography fontSize={48} lineHeight={1} mb={1}>🏆</Typography>
        <Typography variant="h5" fontWeight={700} mb={0.5} color="#ffffff">
          {t('completionCard.headline')}
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.6)" mb={3}>
          {t('completionCard.subline')}
        </Typography>

        {/* Stats row */}
        <Box display="flex" gap={1.5} justifyContent="center" mb={3}>
          <StatTile value={totalReviewed} label={t('completionCard.wordsReviewed')} color="#ffffff" bg="rgba(255,255,255,0.07)" />
          <StatTile value={easyCount} label={t('completionCard.movedToEasy')} color="#3ECFCF" bg="rgba(62,207,207,0.12)" border="rgba(62,207,207,0.35)" />
          <StatTile value={needsWorkCount} label={t('completionCard.needMoreWork')} color="#ff8a8a" bg="rgba(255,100,100,0.1)" border="rgba(255,100,100,0.3)" />
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={onDone}
          sx={{
            borderRadius: 2,
            color: '#ffffff',
            background: 'linear-gradient(135deg, #6C63FF, #3ECFCF)',
            '&:hover': { background: 'linear-gradient(135deg, #5a52e0, #2db8b8)' },
          }}
        >
          {t('completionCard.backToVocabulary')}
        </Button>
      </Paper>
    </Box>
  );
}

interface StatTileProps {
  value: number;
  label: string;
  color: string;
  bg: string;
  border?: string;
}

function StatTile({ value, label, color, bg, border }: StatTileProps) {
  return (
    <Box
      flex={1}
      sx={{
        background: bg,
        border: border ? `1px solid ${border}` : undefined,
        borderRadius: 2,
        p: 1.5,
        textAlign: 'center',
      }}
    >
      <Typography fontSize={28} fontWeight={700} color={color} lineHeight={1}>
        {value}
      </Typography>
      <Typography variant="caption" color={color} display="block" mt={0.25}>
        {label}
      </Typography>
    </Box>
  );
}
